import React, { useEffect, useMemo, useState } from "react"
import { css } from "@emotion/react"
import {
	CustomerPartQueryParams,
	initialCustomerPartQueryParams,
	OnlinePartEcomm,
	useCustomerPartsEcomm,
	usePartChemicals,
	useSearchOnlinePartsEcomm,
} from "@ncs/ncs-api"
import { arrayWrap } from "@ncs/ts-utils"
import {
	Box,
	Button,
	GridContainer,
	GridItem,
	Icon,
	LoadingSpinner,
	VisualSearchCameraCircleIcon,
	Paragraph,
	ResultCountSummaryEcomm,
	useUrlState,
	handleVisualSearch,
	TrackingEvent,
	toggleTrailingSlash,
} from "@ncs/web-legos"
import { Flag, useIsFeatureFlagOn } from "@ncs/web-legos"
import {
	AddedToCartModalEcomm,
	PageContentWrapper,
	PartPreviewRowEcomm,
} from "~/shared-components"
import { PartSearchSidebarEcomm, PartCard, PartSortEcomm, ActiveFiltersEcomm } from "./components"
import { PaginationEcomm } from "./components/PaginationEcomm"
import { SearchFilterModalEcomm } from "./components/Filters/SearchFilterModalEcomm"
import { useAnalytics } from "~/hooks/useAnalytics"
import { useLocation } from "react-router-dom"
import { getPartSearchOrdering } from "./part-search-results-util"
import {
	contentContainer,
	filterButton,
	filterHeader,
	gridPartCard,
	mainContentArea,
	noResultsDescriptionStyle,
	noResultsHeadingStyle,
	noResultsIconBoxStyle,
	partCardStyle,
	resultStyle,
	searchResultsStatusHeaderStyle,
	sideBarFilter,
	snapSearchBoxStyle,
	snapSearchStyle,
	visualSearchDescriptionStyle,
	visualSearchIconBoxStyle,
} from "./styles"

export type PartSearchResultsUrlState = Omit<
	CustomerPartQueryParams,
	"categories" | "manufacturers" | "systems" | "chemicals"
> & {
	// We're changing these types so that the backend doesn't need to JSON stringify these.
	manufacturers: string | number | (string | number)[]
	categories: string | number | (string | number)[]
	systems: string | number | (string | number)[]
	chemicals: string | number | (string | number)[]
	brands: string | number | (string | number)[]
	priceRanges?: string | string[]
	page?: number
}

const PART_NUMBER_SEARCH_REGEX = /^(?=.*\d)[A-Za-z0-9#–—._/-]{3,}$/

const normalizePartNumber = (value: string): string =>
	value.replace(/[\s#–-—._/-]/g, "").toUpperCase()

export const PartSearchResults: React.FC = () => {
	const [addedPart, setAddedPart] = useState<OnlinePartEcomm | null>(null)
	const [params] = useUrlState<PartSearchResultsUrlState>({
		...initialCustomerPartQueryParams,
		page: undefined,
	})
	const { trackEvent } = useAnalytics()
	const { pathname } = useLocation()
	const [chemicalCategories] = usePartChemicals()

	const hideTrending = useIsFeatureFlagOn(Flag.VITE_FLAG_HIDE_TRENDING)
	const effectiveOrdering = useMemo(
		() =>
			getPartSearchOrdering({
				ordering: params.ordering,
				categories: params.categories,
				chemicals: params.chemicals,
				brands: params.brands,
				manufacturers: params.manufacturers,
				chemicalCategories,
			}),
		[
			chemicalCategories,
			params.brands,
			params.categories,
			params.chemicals,
			params.manufacturers,
			params.ordering,
		]
	)

	const {
		data: partResults,
		currentPage,
		resultPageCount,
		isLoading,
		resultCountEstimate,
		fetchPage,
		match,
	} = useSearchOnlinePartsEcomm({
		params: {
			...params,
			ordering: effectiveOrdering,
			categories: arrayWrap(params.categories).map((c) => String(c)),
			manufacturers: arrayWrap(params.manufacturers).map((m) => String(m)),
			systems: arrayWrap(params.systems).map((s) => String(s)),
			chemicals: arrayWrap(params.chemicals).map((s) => String(s)),
			show_children: false,
		},
		pageSize: 24,
		removeChildren: true,
	})

	const parts = useMemo(() => {
		const search = params.search
		if (!search || !PART_NUMBER_SEARCH_REGEX.test(search)) {
			return partResults
		}

		const normalizedSearch = normalizePartNumber(search)
		const matchedPart = partResults.find(
			(part: OnlinePartEcomm) =>
				normalizePartNumber(part.onlinePartNumber) === normalizedSearch
		)

		return matchedPart ? [matchedPart] : []
	}, [partResults, params.search])

	const [showSearchFilterModal, setShowSearchFilterModal] = useState(false)

	const handleVisualSearchOnSearchResults = () => {
		handleVisualSearch()
		trackEvent(TrackingEvent.VISUAL_SEARCH_ICON)
	}

	const handleAddToCart = (selectedPart: OnlinePartEcomm) => {
		trackEvent(TrackingEvent.SR_ADD_TO_CART, {
			id: selectedPart.id,
			item_value: selectedPart.price,
			quantity: 1,
			name: selectedPart.part?.description,
			part_title_name: selectedPart.title,
			part_number: selectedPart.onlinePartNumber,
		})
		setAddedPart(selectedPart)
	}

	const buildPageUrl = (page: number): string => {
		const updatedParams = { ...params, ordering: effectiveOrdering, page }
		const searchParams = new URLSearchParams()

		Object.entries(updatedParams).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== "") {
				if (Array.isArray(value) && value.length > 0) {
					searchParams.set(key, JSON.stringify(value))
				} else if (!Array.isArray(value)) {
					searchParams.set(key, JSON.stringify(value))
				}
			}
		})

		return `${toggleTrailingSlash(pathname)}?${searchParams.toString()}`
	}

	useEffect(() => {
		if (params.page && params.page !== currentPage) {
			fetchPage(params.page)
		} else if (!params.page && currentPage !== 1) {
			fetchPage(1)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		params.page,
		params.search,
		effectiveOrdering,
		params.manufacturers,
		params.categories,
		params.systems,
		params.chemicals,
	])

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" })
	}, [
		params.page,
		params.search,
		effectiveOrdering,
		params.manufacturers,
		params.categories,
		params.systems,
		params.chemicals,
	])

	const shouldShowFallbackSections = !isLoading && parts.length === 0 && match === false
	const shouldShowDidYouMean =
		!isLoading && parts.length === 0 && (match === false || partResults.length > 0)

	const { data: trendingParts = [], isLoading: isLoadingTrendingParts } = useCustomerPartsEcomm({
		params: {
			featured: true,
			ordering: "title",
		},
		pageSize: 10,
		removeChildren: true,
		queryConfig: {
			enabled: shouldShowFallbackSections && !hideTrending,
		},
	})

	const { data: topSearchParts, isLoading: isLoadingTopSearchParts } = useCustomerPartsEcomm({
		params: {
			ordering: "title",
		},
		pageSize: 10,
		removeChildren: true,
		queryConfig: {
			enabled: shouldShowFallbackSections,
		},
	})

	return (
		<PageContentWrapper
			title="Product Search"
			breadcrumbs={[{ name: "Shop", to: "/shop" }, { name: "Search" }]}
		>
			<div css={contentContainer}>
				<div css={sideBarFilter}>
					<PartSearchSidebarEcomm />
				</div>
				<div css={mainContentArea}>
					<div css={searchResultsStatusHeaderStyle}>
						<div css={resultStyle}>
							<ResultCountSummaryEcomm
								currentTotal={parts.length}
								totalResults={parts?.length > 0 ? resultCountEstimate : 0}
								searchItem={params.search}
							/>
						</div>

						<div css={filterHeader}>
							<div css={filterButton}>
								<Button onClick={() => setShowSearchFilterModal(true)}>
									Filters
								</Button>
							</div>
							<PartSortEcomm />
						</div>
					</div>

					<ActiveFiltersEcomm />

					{!isLoading && parts.length === 0 && match !== false && (
						<Box my={2} display="flex" flexDirection="column" alignItems="center">
							<Box
								display="flex"
								width="64px"
								height="64px"
								p="16px"
								justifyContent="center"
								alignItems="center"
								css={noResultsIconBoxStyle}
							>
								<Icon icon="question" color="gray" fontSize={2} />
							</Box>
							<Paragraph mt={1.5} textCenter css={noResultsHeadingStyle}>
								No results for <span>{params.search}</span>
							</Paragraph>
							<Paragraph
								mt={1}
								color="secondary"
								textCenter
								css={noResultsDescriptionStyle}
							>
								We couldn’t find anything matching this search.
							</Paragraph>
						</Box>
					)}

					<Box css={visualSearchIconBoxStyle}>
						<Button onClick={handleVisualSearchOnSearchResults}>
							<VisualSearchCameraCircleIcon />
						</Button>
						<Box css={snapSearchBoxStyle}>
							<p css={snapSearchStyle}>ClickNPic</p>
							<p css={visualSearchDescriptionStyle}>
								Easily find what you need! Simply take or upload a photo, and let
								our AI do the work.
							</p>
						</Box>
					</Box>

					{isLoading && (
						<Box>
							<LoadingSpinner
								text="Searching..."
								py={10}
								height="20px"
								width="20px"
							/>
						</Box>
					)}

					{shouldShowDidYouMean && (
						<div
							css={css`
								border-top: 1px solid #e5e7eb;
								padding-top: 0px;
								margin-top: 20px;
							`}
						>
							<PartPreviewRowEcomm
								heading="Did You Mean"
								partIds={partResults.map((p: OnlinePartEcomm) => p.id)}
								ordering="title"
							/>
						</div>
					)}
					{shouldShowFallbackSections && (
						<>
							{!isLoadingTopSearchParts && (
								<PartPreviewRowEcomm
									heading="Popular Searches"
									partIds={topSearchParts.map((p: OnlinePartEcomm) => p.id)}
									ordering="title"
								/>
							)}

							{!isLoadingTrendingParts && !hideTrending && (
								<PartPreviewRowEcomm
									heading="Trending Products"
									partIds={trendingParts.map((p: OnlinePartEcomm) => p.id)}
								/>
							)}
						</>
					)}

					<br />

					<GridContainer>
						{parts.map((part: OnlinePartEcomm) => (
							<GridItem
								key={part.id}
								xs={12}
								sm={6}
								md={4}
								lg={3}
								css={gridPartCard}
							>
								<PartCard
									part={part}
									onAddToCart={() => handleAddToCart(part)}
									css={partCardStyle}
									widthMode="fluid"
								/>
							</GridItem>
						))}
					</GridContainer>

					{parts.length > 0 && (
						<PaginationEcomm
							numberOfPages={resultPageCount ?? 0}
							buildPageUrl={buildPageUrl}
							currentPage={currentPage ?? 0}
							isLoading={isLoading}
						/>
					)}
				</div>
			</div>

			<AddedToCartModalEcomm
				isOpen={!!addedPart}
				partId={addedPart?.id}
				onClose={() => setAddedPart(null)}
			/>
			<SearchFilterModalEcomm
				isOpen={showSearchFilterModal}
				onClose={() => setShowSearchFilterModal(false)}
			/>
		</PageContentWrapper>
	)
}
